import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Plus, Trash2, Star, Tag, Pencil, Check, X, Droplets, RotateCw, Download, GripVertical, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { API, BACKEND_URL, fileUrl, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RevenueChart from "../components/RevenueChart";

const CATEGORIES = ["Landscape", "Nature", "Portrait", "Street", "Cityscape", "Structure", "Architecture", "Other"];

export default function Admin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [orders, setOrders] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: "", price: "", category: "Landscape", description: "", featured: false, image_url: "" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [dc, setDc] = useState({ code: "", percent: "", expires: "", maxUses: "" });
  const [dcSaving, setDcSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [wm, setWm] = useState({ text: "", strength: 40 });
  const [wmSaving, setWmSaving] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setPwSaving(true);
    try {
      await API.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated — use it next time you sign in");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setPwSaving(false);
    }
  };

  const load = async () => {
    try {
      const [p, o, d, w] = await Promise.all([
        API.get("/admin/photos"),
        API.get("/admin/orders"),
        API.get("/admin/discounts"),
        API.get("/admin/settings/watermark"),
      ]);
      setPhotos(p.data);
      setOrders(o.data);
      setDiscounts(d.data);
      setWm(w.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logout = async () => {
    await API.post("/auth/logout").catch(() => {});
    setUser(null);
    navigate("/login");
  };

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file && !form.image_url.trim()) {
      toast.error("Choose an image file or paste an image URL");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("price", form.price);
      fd.append("category", form.category);
      fd.append("description", form.description);
      fd.append("featured", form.featured);
      fd.append("image_url", form.image_url.trim());
      if (file) fd.append("file", file);
      await API.post("/admin/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photograph added to the gallery");
      setForm({ title: "", price: "", category: "Landscape", description: "", featured: false, image_url: "" });
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (photo) => {
    try {
      await API.delete(`/admin/photos/${photo.id}`);
      toast.success(`"${photo.title}" removed`);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleFeatured = async (photo) => {
    try {
      const { data } = await API.patch(`/admin/photos/${photo.id}`, { featured: !photo.featured });
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? { ...p, featured: data.featured } : p)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const [regenId, setRegenId] = useState(null);

  const regeneratePreview = async (photo) => {
    setRegenId(photo.id);
    try {
      const { data } = await API.post(`/admin/photos/${photo.id}/regenerate-preview`);
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? { ...p, image_url: data.image_url } : p)));
      toast.success(`Preview for "${photo.title}" regenerated with your latest watermark`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRegenId(null);
    }
  };

  const toggleActive = async (photo) => {
    try {
      const { data } = await API.patch(`/admin/photos/${photo.id}`, { active: !photo.active });
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? { ...p, active: data.active } : p)));
      toast.success(data.active ? `"${photo.title}" is live in the gallery` : `"${photo.title}" hidden from the gallery`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveEdit = async (photo) => {
    const price = parseFloat(editPrice);
    const title = editTitle.trim();
    if (!title) {
      toast.error("Enter a title");
      return;
    }
    if (!price || price <= 0) {
      toast.error("Enter a valid price");
      return;
    }
    try {
      const { data } = await API.patch(`/admin/photos/${photo.id}`, { title, price, category: editCategory, description: editDescription.trim() });
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? { ...p, title: data.title, price: data.price, category: data.category, description: data.description } : p)));
      setEditingId(null);
      toast.success(`"${data.title}" updated — $${data.price.toFixed(2)}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const createDiscount = async (e) => {
    e.preventDefault();
    setDcSaving(true);
    try {
      const body = { code: dc.code, percent: parseFloat(dc.percent) };
      if (dc.expires) body.expires_at = dc.expires;
      if (dc.maxUses) body.max_uses = parseInt(dc.maxUses, 10);
      const { data } = await API.post("/admin/discounts", body);
      toast.success(`Code ${data.code} created — ${data.percent}% off`);
      setDc({ code: "", percent: "", expires: "", maxUses: "" });
      setDiscounts((prev) => [data, ...prev]);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setDcSaving(false);
    }
  };

  const toggleDiscount = async (d) => {
    try {
      const { data } = await API.patch(`/admin/discounts/${d.id}`, { active: !d.active });
      setDiscounts((prev) => prev.map((x) => (x.id === d.id ? { ...x, active: data.active } : x)));
      toast.success(data.active ? `Code ${d.code} activated` : `Code ${d.code} paused`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeDiscount = async (d) => {
    try {
      await API.delete(`/admin/discounts/${d.id}`);
      setDiscounts((prev) => prev.filter((x) => x.id !== d.id));
      toast.success(`Code ${d.code} deleted`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveWatermark = async (e) => {
    e.preventDefault();
    setWmSaving(true);
    try {
      const { data } = await API.patch("/admin/settings/watermark", { text: wm.text, strength: Number(wm.strength) });
      setWm(data);
      toast.success("Watermark updated — applies to photos you upload from now on");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setWmSaving(false);
    }
  };

  const dragIdx = useRef(null);

  const dropRow = async (targetIdx) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === targetIdx) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(targetIdx, 0, moved);
    setPhotos(next);
    try {
      await API.post("/admin/photos/reorder", { ordered_ids: next.map((p) => p.id) });
      toast.success("Gallery order saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      await load();
    }
  };

  const regenerateAll = async () => {
    setBulkRunning(true);
    try {
      const { data } = await API.post("/admin/photos/regenerate-all-previews");
      if (!data.total) {
        toast.info("No uploaded photos to regenerate");
      } else {
        toast.success(`Regenerating ${data.total} preview${data.total === 1 ? "" : "s"} in the background — they'll update over the next couple of minutes`);
        setTimeout(load, 20000);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBulkRunning(false);
    }
  };

  const discountMeta = (d) => {
    const parts = [`${d.percent}% off`];
    if (d.expires_at) parts.push(`expires ${new Date(d.expires_at).toLocaleDateString()}`);
    if (d.max_uses) parts.push(`${d.times_redeemed ?? 0}/${d.max_uses} used`);
    else if (d.times_redeemed) parts.push(`${d.times_redeemed} used`);
    return parts.join(" · ");
  };

  const revenue = orders.reduce((s, o) => s + (o.amount || 0), 0);

  const inputCls =
    "w-full bg-black border border-white/15 text-white text-sm px-4 py-3.5 focus:border-[#D4AF37] focus:outline-none transition-colors duration-300";
  const labelCls = "block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2.5";

  return (
    <div className="pt-20 min-h-screen" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-12">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">TGPHOTOSHOP</span>
            <h1 className="font-display text-4xl md:text-5xl text-white tracking-tighter" data-testid="admin-title">Dashboard</h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-white border border-white/15 hover:border-white/40 px-6 py-3 transition-colors duration-300"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12" data-testid="admin-stats">
          {[
            ["Photographs", photos.length],
            ["Sales", orders.length],
            ["Revenue", `$${revenue.toFixed(2)}`],
          ].map(([label, value]) => (
            <div key={label} className="bg-[#0A0A0A] border border-white/5 p-8">
              <span className="text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-3">{label}</span>
              <span className="font-display text-4xl text-white" data-testid={`stat-${label.toLowerCase()}`}>{value}</span>
            </div>
          ))}
        </div>

        <RevenueChart orders={orders} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <form onSubmit={submit} className="bg-[#0A0A0A] border border-white/5 p-8 space-y-5" data-testid="photo-upload-form">
              <h2 className="font-display text-2xl text-white mb-2">Add a photograph</h2>
              <div>
                <label htmlFor="photo-title" className={labelCls}>Title</label>
                <input id="photo-title" data-testid="upload-title-input" required value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="Silent Peaks" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="photo-price" className={labelCls}>Price (AUD)</label>
                  <input id="photo-price" data-testid="upload-price-input" required type="number" min="1" step="0.01" value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} placeholder="199" />
                </div>
                <div>
                  <label htmlFor="photo-category" className={labelCls}>Category</label>
                  <select id="photo-category" data-testid="upload-category-select" value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className={inputCls}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="photo-description" className={labelCls}>Description</label>
                <textarea id="photo-description" data-testid="upload-description-input" rows={3} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} placeholder="The story behind the frame" />
              </div>
              <div>
                <label htmlFor="photo-file" className={labelCls}>Image file</label>
                <input id="photo-file" ref={fileRef} data-testid="upload-file-input" type="file" accept="image/*"
                  className="w-full text-sm text-zinc-400 file:mr-4 file:py-3 file:px-5 file:border file:border-white/20 file:bg-black file:text-zinc-300 file:text-xs file:tracking-[0.15em] file:uppercase hover:file:border-[#D4AF37] file:cursor-pointer" />
              </div>
              <div>
                <label htmlFor="photo-url" className={labelCls}>— or image URL</label>
                <input id="photo-url" data-testid="upload-url-input" value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputCls} placeholder="https://…" />
              </div>
              <label className="flex items-center gap-3 text-sm text-zinc-400 cursor-pointer">
                <input type="checkbox" data-testid="upload-featured-checkbox" checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="w-4 h-4 accent-[#D4AF37]" />
                Feature on the homepage
              </label>
              <p className="text-xs text-zinc-600 leading-relaxed">
                Uploaded originals stay private. Visitors see a watermarked preview —
                buyers receive the untouched file.
              </p>
              <button type="submit" disabled={saving} data-testid="upload-submit-button"
                className="w-full inline-flex items-center justify-center gap-3 bg-white text-black text-xs tracking-[0.25em] uppercase font-semibold px-8 py-4 hover:bg-[#D4AF37] transition-colors duration-300 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}
                Add to gallery
              </button>
            </form>

            <form onSubmit={saveWatermark} className="bg-[#0A0A0A] border border-white/5 p-8 space-y-5 mt-8" data-testid="watermark-form">
              <h2 className="font-display text-2xl text-white mb-2">Watermark</h2>
              <p className="text-xs text-zinc-600 leading-relaxed">
                Shown on previews of uploaded photos. Applies to photos you upload from now on.
              </p>
              <div>
                <label htmlFor="wm-text" className={labelCls}>Text</label>
                <input id="wm-text" data-testid="watermark-text-input" required maxLength={30} value={wm.text}
                  onChange={(e) => setWm({ ...wm, text: e.target.value })} className={inputCls} placeholder="TGPHOTOSHOP" />
              </div>
              <div>
                <label htmlFor="wm-strength" className={labelCls}>Strength — {wm.strength}%</label>
                <input id="wm-strength" data-testid="watermark-strength-input" type="range" min="10" max="100" step="5"
                  value={wm.strength} onChange={(e) => setWm({ ...wm, strength: Number(e.target.value) })}
                  className="w-full accent-[#D4AF37]" />
              </div>
              <button type="submit" disabled={wmSaving} data-testid="watermark-save-button"
                className="w-full inline-flex items-center justify-center gap-3 border border-white/25 text-white text-xs tracking-[0.25em] uppercase font-semibold px-8 py-4 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-60">
                {wmSaving ? <Loader2 className="animate-spin" size={15} /> : <Droplets size={15} />}
                Save watermark
              </button>
              <button type="button" onClick={regenerateAll} disabled={bulkRunning} data-testid="watermark-reapply-all-button"
                className="w-full inline-flex items-center justify-center gap-3 border border-white/10 text-zinc-400 text-xs tracking-[0.25em] uppercase font-semibold px-8 py-4 hover:border-white/40 hover:text-white transition-colors duration-300 disabled:opacity-60">
                {bulkRunning ? <Loader2 className="animate-spin" size={15} /> : <RotateCw size={15} />}
                Reapply to all uploaded photos
              </button>
            </form>

            <form onSubmit={changePassword} className="bg-[#0A0A0A] border border-white/5 p-8 space-y-5 mt-8" data-testid="password-change-form">
              <h2 className="font-display text-2xl text-white mb-2">Change password</h2>
              <div>
                <label htmlFor="pw-current" className={labelCls}>Current password</label>
                <input id="pw-current" data-testid="pw-current-input" required type="password" value={pw.current}
                  onChange={(e) => setPw({ ...pw, current: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label htmlFor="pw-next" className={labelCls}>New password</label>
                <input id="pw-next" data-testid="pw-next-input" required type="password" minLength={8} value={pw.next}
                  onChange={(e) => setPw({ ...pw, next: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label htmlFor="pw-confirm" className={labelCls}>Confirm new password</label>
                <input id="pw-confirm" data-testid="pw-confirm-input" required type="password" value={pw.confirm}
                  onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} />
              </div>
              <button type="submit" disabled={pwSaving} data-testid="pw-submit-button"
                className="w-full inline-flex items-center justify-center gap-3 border border-white/25 text-white text-xs tracking-[0.25em] uppercase font-semibold px-8 py-4 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-60">
                {pwSaving && <Loader2 className="animate-spin" size={15} />}
                Update password
              </button>
            </form>

            <div className="bg-[#0A0A0A] border border-white/5 p-8 mt-8" data-testid="discount-panel">
              <h2 className="font-display text-2xl text-white mb-2">Discount codes</h2>
              <p className="text-xs text-zinc-600 leading-relaxed mb-6">
                Percent-off codes buyers can apply at checkout. Expiry and use limits are optional.
              </p>
              <form onSubmit={createDiscount} className="space-y-5 mb-2" data-testid="discount-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="dc-code" className={labelCls}>Code</label>
                    <input id="dc-code" data-testid="discount-code-input" required value={dc.code}
                      onChange={(e) => setDc({ ...dc, code: e.target.value.toUpperCase() })}
                      className={`${inputCls} uppercase`} placeholder="LAUNCH20" maxLength={20} />
                  </div>
                  <div>
                    <label htmlFor="dc-percent" className={labelCls}>% off</label>
                    <input id="dc-percent" data-testid="discount-percent-input" required type="number" min="1" max="100" value={dc.percent}
                      onChange={(e) => setDc({ ...dc, percent: e.target.value })} className={inputCls} placeholder="20" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="dc-expires" className={labelCls}>Expires</label>
                    <input id="dc-expires" data-testid="discount-expires-input" type="date" value={dc.expires}
                      onChange={(e) => setDc({ ...dc, expires: e.target.value })}
                      className={`${inputCls} [color-scheme:dark]`} />
                  </div>
                  <div>
                    <label htmlFor="dc-max-uses" className={labelCls}>Max uses</label>
                    <input id="dc-max-uses" data-testid="discount-maxuses-input" type="number" min="1" value={dc.maxUses}
                      onChange={(e) => setDc({ ...dc, maxUses: e.target.value })} className={inputCls} placeholder="Unlimited" />
                  </div>
                </div>
                <button type="submit" disabled={dcSaving} data-testid="discount-submit-button"
                  className="w-full inline-flex items-center justify-center gap-3 bg-white text-black text-xs tracking-[0.25em] uppercase font-semibold px-8 py-4 hover:bg-[#D4AF37] transition-colors duration-300 disabled:opacity-60">
                  {dcSaving ? <Loader2 className="animate-spin" size={15} /> : <Tag size={15} />}
                  Create code
                </button>
              </form>
              {discounts.length > 0 && (
                <ul className="divide-y divide-white/5 mt-6 border-t border-white/5">
                  {discounts.map((d) => (
                    <li key={d.id} className="flex items-center justify-between py-4" data-testid={`discount-row-${d.code}`}>
                      <div>
                        <p className="text-sm text-white font-medium tracking-wider">{d.code}</p>
                        <p className="text-xs text-zinc-500 mt-1">{discountMeta(d)}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => toggleDiscount(d)} data-testid={`discount-toggle-${d.code}`}
                          className={`text-xs tracking-[0.15em] uppercase px-3 py-2 border transition-colors duration-300 ${
                            d.active ? "border-[#D4AF37]/50 text-[#D4AF37]" : "border-white/10 text-zinc-600 hover:text-zinc-300"
                          }`}>
                          {d.active ? "Active" : "Paused"}
                        </button>
                        <button onClick={() => removeDiscount(d)} data-testid={`discount-delete-${d.code}`}
                          title="Delete code"
                          className="p-2 text-zinc-600 hover:text-red-400 transition-colors duration-300">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="lg:col-span-8 space-y-8">
            <div className="bg-[#0A0A0A] border border-white/5" data-testid="admin-photos-panel">
              <div className="px-8 py-6 border-b border-white/5 flex items-center justify-between">
                <h2 className="font-display text-2xl text-white">Your photographs</h2>
                <span className="text-xs tracking-[0.2em] uppercase text-zinc-500">{photos.length} total</span>
              </div>
              {loading ? (
                <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-zinc-600" size={24} /></div>
              ) : photos.length === 0 ? (
                <p className="p-12 text-sm text-zinc-500" data-testid="admin-photos-empty">No photographs yet. Add your first one.</p>
              ) : (
                <ul className="divide-y divide-white/5">
                  {photos.map((photo, i) => (
                    <li key={photo.id}
                      draggable
                      onDragStart={() => { dragIdx.current = i; }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => dropRow(i)}
                      className="flex items-center gap-5 px-8 py-4" data-testid={`admin-photo-row-${photo.id}`}>
                      <span className="cursor-grab text-zinc-700 hover:text-zinc-400 transition-colors duration-300 shrink-0"
                        title="Drag to reorder" data-testid={`drag-handle-${photo.id}`}>
                        <GripVertical size={17} />
                      </span>
                      <img src={fileUrl(photo.image_url)} alt={photo.title} className="w-16 h-16 object-cover border border-white/10" />
                      <div className={`flex-1 min-w-0 ${photo.active === false ? "opacity-40" : ""}`}>
                        {editingId === photo.id ? (
                          <div className="space-y-2 py-1">
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              data-testid={`edit-title-input-${photo.id}`}
                              className="w-full max-w-xs bg-black border border-[#D4AF37]/60 text-white text-sm px-3 py-1.5 focus:outline-none"
                              autoFocus
                            />
                            <div className="flex items-center gap-2">
                              <select
                                value={editCategory}
                                onChange={(e) => setEditCategory(e.target.value)}
                                data-testid={`edit-category-select-${photo.id}`}
                                className="bg-black border border-[#D4AF37]/60 text-white text-sm px-2 py-1.5 focus:outline-none"
                              >
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                              </select>
                              <span className="text-xs text-zinc-500">— $</span>
                              <input
                                type="number"
                                min="1"
                                step="0.01"
                                value={editPrice}
                                onChange={(e) => setEditPrice(e.target.value)}
                                data-testid={`edit-price-input-${photo.id}`}
                                className="w-28 bg-black border border-[#D4AF37]/60 text-white text-sm px-3 py-1.5 focus:outline-none"
                              />
                              <button onClick={() => saveEdit(photo)} data-testid={`edit-save-${photo.id}`}
                                title="Save changes"
                                className="p-1.5 text-[#D4AF37] hover:text-white transition-colors duration-300">
                                <Check size={16} />
                              </button>
                              <button onClick={() => setEditingId(null)} data-testid={`edit-cancel-${photo.id}`}
                                title="Cancel"
                                className="p-1.5 text-zinc-600 hover:text-white transition-colors duration-300">
                                <X size={16} />
                              </button>
                            </div>
                            <textarea
                              rows={2}
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              data-testid={`edit-description-input-${photo.id}`}
                              placeholder="Description"
                              className="w-full max-w-md bg-black border border-[#D4AF37]/60 text-white text-sm px-3 py-1.5 focus:outline-none"
                            />
                          </div>
                        ) : (
                          <>
                            <p className="text-sm text-white font-medium truncate">
                              {photo.title}
                              {photo.active === false && (
                                <span className="ml-3 text-[10px] tracking-[0.2em] uppercase text-zinc-600 border border-white/10 px-2 py-0.5">Hidden</span>
                              )}
                            </p>
                            <p className="text-xs text-zinc-500 mt-1">{photo.category} — ${photo.price.toFixed(2)}</p>
                          </>
                        )}
                      </div>
                      {editingId !== photo.id && (
                        <button onClick={() => { setEditingId(photo.id); setEditPrice(String(photo.price)); setEditTitle(photo.title); setEditCategory(photo.category); setEditDescription(photo.description || ""); }}
                          data-testid={`edit-price-${photo.id}`}
                          title="Edit photo details"
                          className="p-2 text-zinc-600 hover:text-[#D4AF37] transition-colors duration-300">
                          <Pencil size={17} />
                        </button>
                      )}
                      {photo.image_url?.startsWith("/api/files/") && (
                        <button onClick={() => regeneratePreview(photo)} disabled={regenId === photo.id}
                          data-testid={`regen-preview-${photo.id}`}
                          title="Regenerate preview with latest watermark"
                          className="p-2 text-zinc-600 hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                          {regenId === photo.id ? <Loader2 size={17} className="animate-spin" /> : <RotateCw size={17} />}
                        </button>
                      )}
                      <button onClick={() => toggleActive(photo)} data-testid={`toggle-active-${photo.id}`}
                        title={photo.active === false ? "Publish to gallery" : "Hide from gallery"}
                        className={`p-2 transition-colors duration-300 ${photo.active === false ? "text-zinc-600 hover:text-white" : "text-zinc-400 hover:text-zinc-200"}`}>
                        {photo.active === false ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                      <button onClick={() => toggleFeatured(photo)} data-testid={`feature-toggle-${photo.id}`}
                        title="Toggle featured"
                        className={`p-2 transition-colors duration-300 ${photo.featured ? "text-[#D4AF37]" : "text-zinc-600 hover:text-zinc-300"}`}>
                        <Star size={17} fill={photo.featured ? "currentColor" : "none"} />
                      </button>
                      <button onClick={() => remove(photo)} data-testid={`delete-photo-${photo.id}`}
                        title="Delete"
                        className="p-2 text-zinc-600 hover:text-red-400 transition-colors duration-300">
                        <Trash2 size={17} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-[#0A0A0A] border border-white/5" data-testid="admin-orders-panel">
              <div className="px-8 py-6 border-b border-white/5 flex items-center justify-between">
                <h2 className="font-display text-2xl text-white">Sales</h2>
                <div className="flex items-center gap-4">
                  <span className="text-xs tracking-[0.2em] uppercase text-zinc-500">{orders.length} orders</span>
                  {orders.length > 0 && (
                    <a
                      href={`${BACKEND_URL}/api/admin/orders/export`}
                      data-testid="export-orders-button"
                      className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-[#D4AF37] border border-white/15 hover:border-[#D4AF37]/50 px-4 py-2 transition-colors duration-300"
                    >
                      <Download size={13} /> Export
                    </a>
                  )}
                </div>
              </div>
              {orders.length === 0 ? (
                <p className="p-12 text-sm text-zinc-500" data-testid="admin-orders-empty">No sales yet. They will appear here after checkout.</p>
              ) : (
                <ul className="divide-y divide-white/5">
                  {orders.map((order) => (
                    <li key={order.id} className="flex items-center justify-between px-8 py-4" data-testid={`admin-order-row-${order.id}`}>
                      <div>
                        <p className="text-sm text-white font-medium">{order.photo_title}</p>
                        <p className="text-xs text-zinc-500 mt-1">{order.buyer_email ? `${order.buyer_email} — ` : ""}{new Date(order.created_at).toLocaleString()}</p>
                      </div>
                      <span className="text-sm text-[#D4AF37] font-medium">${(order.amount || 0).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
